import type { Breadcrumb, ErrorEvent, StackFrame } from '@sentry/react';

/** Breadcrumbs may describe timing and categories, never typed text or DOM content. */
export function safeBreadcrumb(crumb: Breadcrumb): Breadcrumb | null {
  if (crumb.category === 'ui.input' || crumb.message !== undefined || crumb.data !== undefined) return null;
  if (!['navigation', 'http', 'ui.click'].includes(crumb.category ?? '')) return null;
  return { category: crumb.category, type: crumb.type, level: crumb.level, timestamp: crumb.timestamp };
}

/** A stack frame says where the code was, never what it held: no variables, no source around it. */
function safeFrame(frame: StackFrame): StackFrame {
  return {
    filename: frame.filename,
    function: frame.function,
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  };
}

/**
 * Error messages and arbitrary context can quote form input, so the report is built from an allowlist: the
 * error's type and where it happened (its stack), never its message, extra context, user or request.
 */
export function safeEvent(event: ErrorEvent): ErrorEvent {
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    platform: 'javascript',
    release: event.release,
    environment: event.environment,
    exception: event.exception
      ? {
          values: event.exception.values?.map((value) => ({
            type: value.type ?? 'Error',
            value: 'Web app error',
            ...(value.stacktrace?.frames ? { stacktrace: { frames: value.stacktrace.frames.map(safeFrame) } } : {}),
          })),
        }
      : undefined,
    breadcrumbs: event.breadcrumbs?.flatMap((crumb) => {
      const safe = safeBreadcrumb(crumb);
      return safe ? [safe] : [];
    }),
  };
}

/**
 * Error reporting for the web app, only when a DSN is set (and only then is the SDK downloaded).
 * No personal or tax data is ever sent.
 */
export async function initSentry(): Promise<boolean> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return false;
  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    beforeBreadcrumb: safeBreadcrumb,
    beforeSend: safeEvent,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
  return true;
}
